import {
  Button,
  EmailCompose,
  EmailInbox,
  EmailRightPanel,
  EmailThread,
  EmailTopBarActions,
  Icon,
  setGmailFromUrl,
  useGmailStore,
  useToast,
  type EmailComposeSendPayload,
  type EmailReplySendPayload,
  type EmAttachment,
  type EmThread,
} from '@elva/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { extractApiErrorMessage } from '@/api/errors';
import { ElvaSectionState } from '@/components/ElvaSectionState';
import { isGmailAttachmentId } from '../api/emailAttachmentDownload';
import { emailApi } from '../api/emailApi';
import { adaptEmailMessage, adaptEmailMessageDetail, readEmailProviderThreadId } from '../adapters/emailAdapters';
import { useEmailComposeSend } from '../hooks/useEmailComposeSend';
import { useEmailAutopilot, useSetEmailAutopilot } from '../hooks/useEmailAutopilot';
import { useApproveDraft, useEmailMessage, useReplySend, useTranslateThread } from '../hooks/useEmailMessage';
import { useEmailMessages } from '../hooks/useEmailMessages';
import { useEmailThreadRead } from '../hooks/useEmailThreadRead';
import { useEmailThreadSync } from '../hooks/useEmailThreadSync';

const GMAIL_CARD = '/settings/ai/channels/email#gmail';

const EMPTY_THREAD: EmThread = {
  id: '',
  subject: '',
  name: '',
  email: '',
  initials: '',
  lang: 'en',
  verified: 'unknown',
  snippet: '',
  when: '',
  unread: false,
  aiState: 'none',
  currentTopic: null,
  tags: [],
  suggested: [],
  patient: {
    name: '',
    email: '',
    initials: '',
    lang: 'en',
    status: '',
    balance: '$0',
    insurance: { plan: '', status: '', remaining: 0, expires: '' },
    consent: { email: true, sms: true },
  },
  summary: '',
  messages: [],
  audit: [],
};

export function EmailPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const gmailParam = params.get('gmail');
  const roleParam = params.get('role');
  useEffect(() => {
    setGmailFromUrl(gmailParam, roleParam);
  }, [gmailParam, roleParam]);
  const { state: gmail, admin } = useGmailStore();
  const { data, isLoading, isError } = useEmailMessages();
  const rawMessages = data?.threads ?? data?.results ?? data?.messages ?? [];
  const threads = useMemo(
    () => rawMessages.map(adaptEmailMessage),
    [rawMessages],
  );

  const [activeId, setActiveId] = useState('');
  useEffect(() => {
    if (threads.length > 0 && !threads.some((t) => t.id === activeId)) {
      setActiveId(threads[0]!.id);
    }
  }, [threads, activeId]);

  const listThread = useMemo(
    () => threads.find((t) => t.id === activeId),
    [threads, activeId],
  );

  const { data: detailData } = useEmailMessage(activeId || null);

  const providerThreadId = useMemo(() => {
    const fromDetail = readEmailProviderThreadId(detailData);
    if (fromDetail) return fromDetail;
    const raw = rawMessages.find((row) => {
      const id = typeof row === 'object' && row && 'id' in row ? String((row as { id: unknown }).id) : '';
      return id === activeId;
    });
    return readEmailProviderThreadId(raw);
  }, [activeId, detailData, rawMessages]);

  useEmailThreadSync(activeId || null);
  useEmailThreadRead(activeId || null, providerThreadId);
  const replySend = useReplySend(activeId);
  const approveDraft = useApproveDraft(activeId);
  const translateThread = useTranslateThread(activeId);
  const composeSend = useEmailComposeSend();
  const thread = useMemo(() => {
    if (!listThread) return EMPTY_THREAD;
    if (detailData) return adaptEmailMessageDetail(detailData, listThread);
    return listThread;
  }, [listThread, detailData]);

  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMin, setComposeMin] = useState(false);
  const [intelOpen, setIntelOpen] = useState(false);

  const { data: autopilot } = useEmailAutopilot();
  const setAutopilot = useSetEmailAutopilot();
  const autopilotOn = autopilot?.email_autopilot_enabled ?? false;

  const handleSelectThread = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  const handleSendReply = useCallback(
    (payload: EmailReplySendPayload) => {
      if (!activeId) return;
      const hasDraftForReview = Boolean(thread.draft && !thread.draft.autoSent);
      const hasAttachments = Boolean(payload.attachments?.length);
      const send = hasDraftForReview && !hasAttachments ? approveDraft : replySend;
      void send
        .mutateAsync(payload)
        .catch((err: unknown) => {
          toast({
            title: 'Send failed',
            description: extractApiErrorMessage(err),
            variant: 'error',
          });
        });
    },
    [activeId, approveDraft, replySend, thread.draft, toast],
  );

  const handleComposeSend = useCallback(
    (payload: EmailComposeSendPayload) => {
      void composeSend
        .mutateAsync(payload)
        .then(({ messageId }) => {
          setActiveId(messageId);
          setComposeOpen(false);
          setComposeMin(false);
          toast({ title: 'Email sent', variant: 'success' });
        })
        .catch((err: unknown) => {
          toast({
            title: 'Send failed',
            description: extractApiErrorMessage(err),
            variant: 'error',
          });
        });
    },
    [composeSend, toast],
  );

  const handleTranslate = useCallback(
    (options?: { force?: boolean }) => {
      if (!activeId) return;
      void translateThread
        .mutateAsync(options)
        .catch((err: unknown) => {
          toast({
            title: 'Translation failed',
            description: extractApiErrorMessage(err),
            variant: 'error',
          });
        });
    },
    [activeId, translateThread, toast],
  );

  const resolveAttachmentContext = useCallback(
    (attachment: EmAttachment, messageId: string) => ({
      perMessageId: attachment.messageId ?? messageId,
      threadId: activeId,
    }),
    [activeId],
  );

  const openAttachment = useCallback(
    async (attachment: EmAttachment, messageId: string) => {
      if (attachment.previewUrl) {
        window.open(attachment.previewUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      if (!attachment.id) return;

      const { perMessageId, threadId } = resolveAttachmentContext(attachment, messageId);
      if (isGmailAttachmentId(attachment.id) && !perMessageId) return;

      try {
        const blob = await emailApi.getAttachment(attachment.id, perMessageId, threadId);
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (err: unknown) {
        toast({
          title: 'Preview failed',
          description: extractApiErrorMessage(err),
          variant: 'error',
        });
      }
    },
    [resolveAttachmentContext, toast],
  );

  const downloadAttachment = useCallback(
    async (attachment: EmAttachment, messageId: string) => {
      const triggerDownload = (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.name || 'attachment';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      };

      if (attachment.previewUrl?.startsWith('blob:') && !attachment.id) {
        try {
          triggerDownload(await fetch(attachment.previewUrl).then((response) => response.blob()));
        } catch (err: unknown) {
          toast({
            title: 'Download failed',
            description: extractApiErrorMessage(err),
            variant: 'error',
          });
        }
        return;
      }

      if (!attachment.id) return;

      const { perMessageId, threadId } = resolveAttachmentContext(attachment, messageId);
      if (isGmailAttachmentId(attachment.id) && !perMessageId) return;

      try {
        const blob = await emailApi.getAttachment(attachment.id, perMessageId, threadId);
        triggerDownload(blob);
      } catch (err: unknown) {
        if (attachment.previewUrl?.startsWith('blob:')) {
          try {
            triggerDownload(await fetch(attachment.previewUrl).then((response) => response.blob()));
            return;
          } catch {
            // fall through to toast
          }
        }
        toast({
          title: 'Download failed',
          description: extractApiErrorMessage(err),
          variant: 'error',
        });
      }
    },
    [resolveAttachmentContext, toast],
  );

  const loadAttachmentPreview = useCallback(
    async (attachment: EmAttachment, messageId: string) => {
      if (attachment.previewUrl) return attachment.previewUrl;
      if (!attachment.id) return null;

      const { perMessageId, threadId } = resolveAttachmentContext(attachment, messageId);
      if (isGmailAttachmentId(attachment.id) && !perMessageId) return null;
      const isImage =
        attachment.contentType?.startsWith('image/') ||
        /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(attachment.name);
      if (!isImage) return null;
      try {
        const blob = await emailApi.getAttachment(attachment.id, perMessageId, threadId);
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    },
    [resolveAttachmentContext],
  );

  usePageTopBar({
    actions: (
      <EmailTopBarActions
        threads={threads}
        autopilotOn={autopilotOn}
        autopilotBusy={setAutopilot.isPending}
        onToggleAutopilot={() => setAutopilot.mutate(!autopilotOn)}
        onCompose={() => {
          setComposeOpen(true);
          setComposeMin(false);
        }}
      />
    ),
  });

  if (gmail === 'none') {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center">
        <div className="grid size-14 place-items-center rounded-full bg-[var(--surface-mute)] text-[var(--ink-3)]">
          <Icon kind="mail" size={24} stroke={1.8} />
        </div>
        <h2 className="mt-4 text-[17px] font-semibold text-[var(--ink-0)]">Connect your Gmail to see patients’ emails here</h2>
        <p className="mt-1 max-w-[440px] text-[13px] text-[var(--ink-3)]">ELVA reads the inbox and answers patients from it. One account.</p>
        <div className="mt-4">
          {admin ? (
            <Button variant="primary" size="sm" iconLeft="mail" onClick={() => { void navigate(GMAIL_CARD); }}>Connect Gmail</Button>
          ) : (
            <Button variant="secondary" size="sm" disabled>Ask an admin to connect Gmail</Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {gmail === 'expired' ? (
        <div role="status" className="flex flex-wrap items-center gap-3 border-b border-[color-mix(in_srgb,var(--alert-red)_30%,transparent)] bg-[#FBEDEC] px-4 py-2.5">
          <Icon kind="alert" size={15} stroke={2} className="shrink-0 text-[var(--alert-red)]" />
          <span className="text-[12.5px] font-semibold text-[var(--alert-red)]">Gmail needs reconnecting. ELVA isn’t reading or answering new emails since 30 Sep, 4:12 pm.</span>
          <button type="button" className="ml-auto text-[12.5px] font-semibold text-[var(--alert-red)] hover:underline" onClick={() => { void navigate(GMAIL_CARD); }}>Reconnect</button>
        </div>
      ) : null}
      <div className="relative flex min-h-0 flex-1 overflow-x-auto overflow-y-hidden">
        <EmailInbox activeId={activeId} onSelect={handleSelectThread} threads={threads} />
        <ElvaSectionState isLoading={isLoading && threads.length === 0} isError={isError}>
          {activeId && thread.id ? (
            <>
              <EmailThread
                thread={thread}
                intelOpen={intelOpen}
                onToggleIntel={() => setIntelOpen((v) => !v)}
                onOpenAttachment={openAttachment}
                onDownloadAttachment={downloadAttachment}
                loadAttachmentPreview={loadAttachmentPreview}
                onSendReply={handleSendReply}
                sendingReply={replySend.isPending || approveDraft.isPending}
                onTranslate={handleTranslate}
                translating={translateThread.isPending}
              />
              <EmailRightPanel thread={thread} open={intelOpen} onClose={() => setIntelOpen(false)} />
            </>
          ) : (
            <div className="flex min-h-0 flex-1 items-center justify-center bg-[var(--cream-0)] text-[13px] text-[var(--ink-3)]">
              {isLoading ? 'Loading inbox…' : 'Select a conversation to read'}
            </div>
          )}
        </ElvaSectionState>
      </div>
      {composeOpen ? (
        <EmailCompose
          minimized={composeMin}
          onMinimize={() => setComposeMin(true)}
          onRestore={() => setComposeMin(false)}
          onClose={() => {
            setComposeOpen(false);
            setComposeMin(false);
          }}
          onSend={handleComposeSend}
          sending={composeSend.isPending}
        />
      ) : null}
    </div>
  );
}
